
(function () {
  if (customElements.get('video-showcase')) return;

  const MOBILE_QUERY = '(max-width: 767px)';

  class VideoShowcase extends HTMLElement {
    connectedCallback() {
      this.blocks = Array.from(this.querySelectorAll('[data-video-block]'));
      this.tabs = Array.from(this.querySelectorAll('[data-mobile-tab]'));
      this.mobileWraps = Array.from(this.querySelectorAll('[data-mobile-video]'));
      if (!this.blocks.length && !this.mobileWraps.length) return;

      this.activeIndex = 0;
      this.mobileIndex = 0;
      this._cleanups = [];

      this.mql = window.matchMedia(MOBILE_QUERY);
      this.isMobile = this.mql.matches;
      this.onBreakpoint = (e) => { this.isMobile = e.matches; };
      this.mql.addEventListener('change', this.onBreakpoint);

      this.setupDesktop();
      this.setupMobile();

      // Play when the section is on screen, pause when it scrolls away.
      this.io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) this.playActive();
            else this.pauseAll();
          }
        },
        { threshold: 0.1 },
      );
      this.io.observe(this);
    }

    disconnectedCallback() {
      this.io?.disconnect();
      this.mql?.removeEventListener('change', this.onBreakpoint);
      this._cleanups.forEach((off) => off());
      this._cleanups = [];
      this.pauseAll();
    }

    // Bind a listener and remember how to remove it.
    on(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this._cleanups.push(() => target.removeEventListener(type, handler, options));
    }

    videoOf(el) {
      return el ? el.querySelector('video') : null;
    }

    /* ---------------------------------- desktop --------------------------------- */

    setupDesktop() {
      this.players = this.blocks.map((b) => this.videoOf(b));
      this.progressBars = this.blocks.map((b) => b.querySelector('[data-video-progress-bar]'));

      this.blocks.forEach((block, index) => {
        const player = this.players[index];
        if (player) {
          player.muted = true;
          this.on(player, 'timeupdate', () => this.updateProgress(this.progressBars[index], player));
          this.on(player, 'ended', () => {
            if (!player.loop) this.activateDesktop(this.nextIndex(index, this.blocks.length));
          });
          this.on(player, 'play', () => {
            this.pauseOthers(this.players, index);
            this.setOverlay(block, '.video-overlay', false);
          });
          this.on(player, 'pause', () => this.setOverlay(block, '.video-overlay', true));
        }

        const overlay = block.querySelector('.video-overlay');
        if (overlay) {
          this.on(overlay, 'click', (e) => {
            e.stopPropagation();
            if (index === this.activeIndex) this.togglePlay(this.players[index]);
            else this.activateDesktop(index);
          });
        }

        this.on(block, 'click', () => this.activateDesktop(index));
      });
    }

    activateDesktop(index) {
      if (index === this.activeIndex) return;

      this.players[this.activeIndex]?.pause();
      this.setOverlay(this.blocks[this.activeIndex], '.video-overlay', true);

      this.blocks.forEach((block, i) => {
        const active = i === index;
        block.classList.toggle('active', active);
        block.classList.toggle('t-flex-[2]', active);
        block.classList.toggle('t-flex-1', !active);
        block.querySelector('.video-info p')?.classList.toggle('t-hidden', !active);
        block.querySelector('[data-video-progress-container]')?.classList.toggle('t-hidden', !active);
      });

      const player = this.players[index];
      if (player) {
        player.currentTime = 0;
        this.resetProgress(this.progressBars[index]);
        this.setOverlay(this.blocks[index], '.video-overlay', false);
        this.play(player);
      }

      this.activeIndex = index;
    }

    /* ---------------------------------- mobile ---------------------------------- */

    setupMobile() {
      this.mobilePlayers = this.mobileWraps.map((w) => this.videoOf(w));
      this.mobileProgressBars = this.mobileWraps.map((w) => w.querySelector('[data-mobile-video-progress-bar]'));

      this.mobileWraps.forEach((wrap, index) => {
        const player = this.mobilePlayers[index];
        if (player) {
          player.muted = true;
          this.on(player, 'timeupdate', () => this.updateProgress(this.mobileProgressBars[index], player));
          this.on(player, 'ended', () => this.activateMobile(this.nextIndex(index, this.mobileWraps.length)));
          this.on(player, 'play', () => this.setOverlay(wrap, '.mobile-video-overlay', false));
          this.on(player, 'pause', () => this.setOverlay(wrap, '.mobile-video-overlay', true));
        }

        const overlay = wrap.querySelector('.mobile-video-overlay');
        if (overlay) this.on(overlay, 'click', () => this.togglePlay(this.mobilePlayers[index]));
      });

      this.tabs.forEach((tab, index) => {
        this.on(tab, 'click', (e) => {
          e.preventDefault();
          this.activateMobile(index);
        });
      });
    }

    activateMobile(index) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.classList.toggle('active', active);
        tab.classList.toggle('t-text-White', active);
        tab.classList.toggle('t-text-[rgba(255,255,255,0.5)]', !active);
      });

      this.mobilePlayers[this.mobileIndex]?.pause();

      this.mobileWraps.forEach((wrap, i) => {
        const active = i === index;
        wrap.classList.toggle('t-opacity-0', !active);
        if (!active) return;

        const player = this.mobilePlayers[i];
        if (player) {
          player.currentTime = 0;
          this.resetProgress(this.mobileProgressBars[i]);
          this.setOverlay(wrap, '.mobile-video-overlay', false);
          this.play(player);
        }
      });

      this.mobileIndex = index;
    }

    /* ---------------------------------- shared ---------------------------------- */

    playActive() {
      this.play(this.isMobile ? this.mobilePlayers?.[this.mobileIndex] : this.players?.[this.activeIndex]);
    }

    pauseAll() {
      this.players?.forEach((p) => p?.pause());
      this.mobilePlayers?.forEach((p) => p?.pause());
    }

    pauseOthers(list, keepIndex) {
      list.forEach((p, i) => {
        if (i !== keepIndex) p?.pause();
      });
    }

    play(player) {
      const result = player?.play();
      if (result && typeof result.catch === 'function') result.catch(() => {});
    }

    togglePlay(player) {
      if (!player) return;
      if (player.paused) this.play(player);
      else player.pause();
    }

    setOverlay(scope, selector, show) {
      const overlay = scope?.querySelector(selector);
      if (overlay) overlay.style.display = show ? 'block' : 'none';
    }

    updateProgress(bar, player) {
      if (!bar || !player || !player.duration || isNaN(player.duration)) return;
      bar.style.width = `${(player.currentTime / player.duration) * 100}%`;
    }

    resetProgress(bar) {
      if (bar) bar.style.width = '0%';
    }

    nextIndex(current, length) {
      return length ? (current + 1) % length : 0;
    }
  }

  customElements.define('video-showcase', VideoShowcase);
})();
